class CartDrawer extends HTMLElement {
	constructor() {
		super();

		this.addEventListener(
			"keyup",
			(evt) => evt.code === "Escape" && this.close()
		);
		this.querySelector("#CartDrawer-Overlay").addEventListener(
			"click",
			this.close.bind(this)
		);
		this.setHeaderCartIconAccessibility();
	}

	setHeaderCartIconAccessibility() {
		const cartLink = document.querySelector("#cart-icon-bubble");
		cartLink.setAttribute("role", "button");
		cartLink.setAttribute("aria-haspopup", "dialog");
		cartLink.addEventListener("click", (event) => {
			event.preventDefault();
			this.open(cartLink);
		});
		cartLink.addEventListener("keydown", (event) => {
			if (event.code.toUpperCase() === "SPACE") {
				event.preventDefault();
				this.open(cartLink);
			}
		});
	}

	open(triggeredBy) {
		if (triggeredBy) this.setActiveElement(triggeredBy);
		const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
		if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
			this.setSummaryAccessibility(cartDrawerNote);
		// here the animation doesn't seem to always get triggered. A timeout seem to help
		setTimeout(() => {
			this.classList.add("animate", "active");
		});

		this.addEventListener(
			"transitionend",
			() => {
				const containerToTrapFocusOn = this.classList.contains("is-empty")
					? this.querySelector(".drawer__inner-empty")
					: document.getElementById("CartDrawer");
				const focusElement =
					this.querySelector(".drawer__inner") ||
					this.querySelector(".drawer__close");
				trapFocus(containerToTrapFocusOn, focusElement);
			},
			{ once: true }
		);

		document.body.classList.add("overflow-hidden");
	}

	close() {
		this.classList.remove("active");
		removeTrapFocus(this.activeElement);
		document.body.classList.remove("overflow-hidden");
	}

	setSummaryAccessibility(cartDrawerNote) {
		cartDrawerNote.setAttribute("role", "button");
		cartDrawerNote.setAttribute("aria-expanded", "false");

		if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
			cartDrawerNote.setAttribute(
				"aria-controls",
				cartDrawerNote.nextElementSibling.id
			);
		}

		cartDrawerNote.addEventListener("click", (event) => {
			event.currentTarget.setAttribute(
				"aria-expanded",
				!event.currentTarget.closest("details").hasAttribute("open")
			);
		});

		cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
	}

	renderContents(parsedState) {
		this.querySelector(".drawer__inner").classList.contains("is-empty") &&
			this.querySelector(".drawer__inner").classList.remove("is-empty");
		this.productId = parsedState.id;
		this.getSectionsToRender().forEach((section) => {
			const sectionElement = section.selector
				? document.querySelector(section.selector)
				: document.getElementById(section.id);
			sectionElement.innerHTML = this.getSectionInnerHTML(
				parsedState.sections[section.id],
				section.selector
			);
		});

		setTimeout(() => {
			this.querySelector("#CartDrawer-Overlay").addEventListener(
				"click",
				this.close.bind(this)
			);
			this.open();
		});
	}

	async refreshAfterHistoryNavigation() {
		if (this.historyRefreshPromise) return this.historyRefreshPromise;

		const root =
			(window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
			"/";
		const sectionIds = this.getSectionsToRender().map((section) => section.id);
		const url = `${root}?sections=${sectionIds.join(",")}`;

		this.historyRefreshPromise = fetch(url, {
			credentials: "same-origin",
			cache: "no-store",
			headers: {
				Accept: "application/json",
				"X-Requested-With": "XMLHttpRequest",
			},
		})
			.then((response) => {
				if (!response.ok) throw new Error(`Cart refresh failed (${response.status})`);
				return response.json();
			})
			.then((sections) => {
				const drawerHTML = sections["cart-drawer"];
				const iconHTML = sections["cart-icon-bubble"];
				if (!drawerHTML || !iconHTML) throw new Error("Cart refresh sections missing");

				const drawerDocument = new DOMParser().parseFromString(
					drawerHTML,
					"text/html"
				);
				const nextDrawer = drawerDocument.querySelector("cart-drawer");
				const nextDrawerContents = drawerDocument.querySelector("#CartDrawer");
				const nextDrawerOverlay = drawerDocument.querySelector(
					"#CartDrawer-Overlay"
				);
				const currentDrawerContents = this.querySelector("#CartDrawer");
				const currentDrawerOverlay = this.querySelector("#CartDrawer-Overlay");
				if (
					!nextDrawer ||
					!nextDrawerContents ||
					!nextDrawerOverlay ||
					!currentDrawerContents ||
					!currentDrawerOverlay
				) {
					throw new Error("Cart drawer markup missing");
				}

				const iconDocument = new DOMParser().parseFromString(
					iconHTML,
					"text/html"
				);
				const nextIconSection = iconDocument.querySelector(".shopify-section");
				const currentIcon = document.getElementById("cart-icon-bubble");
				if (!nextIconSection || !currentIcon) {
					throw new Error("Cart icon markup missing");
				}

				const isEmpty = nextDrawer.classList.contains("is-empty");
				if (this.classList.contains("active")) this.close();
				currentDrawerOverlay.replaceWith(nextDrawerOverlay);
				currentDrawerContents.replaceWith(nextDrawerContents);
				nextDrawerOverlay.addEventListener("click", this.close.bind(this));
				currentIcon.innerHTML = nextIconSection.innerHTML;
				this.classList.toggle("is-empty", isEmpty);
				currentIcon.classList.toggle("header__cart--empt", isEmpty);
				document.dispatchEvent(
					new CustomEvent("cart:change", {
						detail: { source: "history-navigation" },
					})
				);
			})
			.catch((error) => {
				console.error("Unable to refresh cart after history navigation:", error);
			})
			.finally(() => {
				this.historyRefreshPromise = null;
			});

		return this.historyRefreshPromise;
	}

	getSectionInnerHTML(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector).innerHTML;
	}

	getSectionsToRender() {
		return [
			{
				id: "cart-drawer",
				selector: "#CartDrawer",
			},
			{
				id: "cart-icon-bubble",
			},
		];
	}

	getSectionDOM(html, selector = ".shopify-section") {
		return new DOMParser()
			.parseFromString(html, "text/html")
			.querySelector(selector);
	}

	setActiveElement(element) {
		this.activeElement = element;
	}
}

customElements.define("cart-drawer", CartDrawer);

window.addEventListener("pageshow", (event) => {
	const navigationEntry = performance.getEntriesByType("navigation")[0];
	const restoredFromHistory =
		event.persisted || navigationEntry?.type === "back_forward";
	if (!restoredFromHistory) return;

	const cartDrawer = document.querySelector("cart-drawer");
	if (typeof cartDrawer?.refreshAfterHistoryNavigation === "function") {
		cartDrawer.refreshAfterHistoryNavigation();
	}
});

class CartDrawerItems extends CartItems {
	getSectionsToRender() {
		return [
			{
				id: "CartDrawer",
				section: "cart-drawer",
				selector: ".drawer__inner",
			},
			{
				id: "cart-icon-bubble",
				section: "cart-icon-bubble",
				selector: ".shopify-section",
			},
		];
	}
}

customElements.define("cart-drawer-items", CartDrawerItems);
